import { useScreenLock } from "@/components/ScreenLock";
import { NonFieldError } from "@/components/common/SCAlert";
import { ScrollToTop } from "@/components/common/ScrollToTop";
import {
  getProjectParticipationInfo,
  useParticipationJoinAPI,
} from "@/components/examinee/participation/ParticipationAPI";
import { ParticipationUrlInfo } from "@/components/examinee/participation/Types";
import { GeneralError } from "@/components/sleep_checkup_v1/GeneralError";
import { BirthdayForm, TextForm } from "@/components/sleep_checkup_v1/TextForm";
import { validateBirthday } from "@/utils/date";
import { ProjectNameCard } from "@/pages/examinee/participation/ProjectGreetings";
import { Button, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * プロジェクト固有 URL からのログイン参加ページ（要ログイン）。
 * 姓・名・生年月日を入力して参加すると、事前 CSV 登録済みレコードに紐付けられる
 * （受付番号の入力は廃止）。
 */
export const ProjectJoin = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const joinAPI = useParticipationJoinAPI();
  const urlId = new URLSearchParams(location.search).get("url_id");

  const [info, setInfo] = useState<ParticipationUrlInfo | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [lastName, setLastName] = useState("");
  const [lastNameErrorMessages, setLastNameErrorMessages] = useState<string[]>(
    []
  );
  const [firstName, setFirstName] = useState("");
  const [firstNameErrorMessages, setFirstNameErrorMessages] = useState<
    string[]
  >([]);
  /** BirthdayForm と同じ表記（入力中は "YYYYMMDD"、確定後は "YYYY/MM/DD"） */
  const [birthday, setBirthday] = useState("");
  const [birthdayErrorMessages, setBirthdayErrorMessages] = useState<string[]>(
    []
  );
  const [isLocked, setLock] = useScreenLock();

  useEffect(() => {
    if (!urlId) {
      return;
    }
    // プロジェクト名の表示用（失敗しても参加操作には影響させない）
    getProjectParticipationInfo(urlId)
      .then((res) => setInfo(res.data))
      .catch(() => setInfo(null));
  }, [urlId]);

  if (!urlId) {
    return <GeneralError to="" />;
  }

  const validators: Record<string, () => string[] | null> = {
    lastName: () => (lastName.trim() ? null : ["※姓を入力してください"]),
    firstName: () => (firstName.trim() ? null : ["※名を入力してください"]),
    birthday: () => validateBirthday(birthday.split("/").join("")),
  };

  const handleClickJoin = () => {
    if (isLocked) {
      return;
    }
    // 前回送信時のサーバエラーをクリアする。表示先は応答ごとに 1 箇所に絞るため、
    // フィールドエラーと NonFieldError が同時に残留しないようにする
    // （クライアント側バリデーションで弾かれる場合も旧エラーを残さない）
    setErrorMessage("");
    setLastNameErrorMessages([]);
    setFirstNameErrorMessages([]);
    setBirthdayErrorMessages([]);
    if (Object.values(validators).some((v) => v())) {
      // 何かしらのエラーがある場合は処理を行わない
      return;
    }
    setLock(true);
    // BirthdayForm の確定表記 "YYYY/MM/DD" を API の "YYYY-MM-DD" に変換する
    const birthdayWithHyphen = birthday.replace(
      /(\d{4})\/(\d{2})\/(\d{2})/,
      "$1-$2-$3"
    );
    // 前後の空白は送信前に除く（内部空白・全角/半角の差は BE 側の正規化で吸収される）
    joinAPI
      .projectJoin(urlId, lastName.trim(), firstName.trim(), birthdayWithHyphen)
      .then(() => {
        navigate("/examinee/mypage");
      })
      .catch((err) => {
        const statusCode = err.response?.status;
        if (statusCode === 409) {
          // 既に参加済みの場合はマイページへ誘導する
          navigate("/examinee/mypage");
          return;
        }
        setLock(false); // エラー時はロックを解放する。
        const messages = err.response?.data?.error_messages;
        const lastNameErrors = err.response?.data?.last_name;
        const firstNameErrors = err.response?.data?.first_name;
        const birthdayErrors = err.response?.data?.birthday;
        if (lastNameErrors || firstNameErrors || birthdayErrors) {
          if (lastNameErrors) {
            setLastNameErrorMessages(lastNameErrors);
          }
          if (firstNameErrors) {
            setFirstNameErrorMessages(firstNameErrors);
          }
          if (birthdayErrors) {
            setBirthdayErrorMessages(birthdayErrors);
          }
        } else if (
          err.response?.data?.error_code === "examinee_identity_mismatch"
        ) {
          // 照合失敗は姓・名・生年月日のどれが原因かも含めて区別されずに返る。
          // 3 フィールドに跨がるためページ上部の NonFieldError に表示する
          setErrorMessage(
            "氏名または生年月日が確認できません。実施企業・団体に登録されているお名前と生年月日をご確認ください"
          );
        } else if (
          statusCode &&
          400 <= statusCode &&
          statusCode < 500 &&
          messages &&
          messages.length > 0
        ) {
          setErrorMessage(messages[0]);
        } else {
          setErrorMessage(
            "エラーが発生しました。申し訳ありませんが、少し時間をおいてから再度お試しください。"
          );
        }

        // エラーアラートが画面内に表示されるようにスクロールをリセットする
        window.scrollTo(0, 0);
      });
  };

  return (
    <>
      <ScrollToTop />
      <Stack spacing={8} sx={{ mt: 8, mb: 12, mx: 4 }}>
        <Stack spacing={6}>
          <Typography variant="h6" align="center">
            測定への参加
          </Typography>

          {/* エラーメッセージ */}
          <NonFieldError>{errorMessage}</NonFieldError>

          {info && <ProjectNameCard info={info} />}

          <Typography
            variant="subtitle2"
            textAlign="justify"
            sx={{ color: "text.secondary" }}
          >
            測定に参加するため、姓、名、生年月日を入力し、[参加する]ボタンを押してください。
          </Typography>

          <TextForm
            title="姓"
            placeholder="例：睡眠"
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value);
              setLastNameErrorMessages([]);
            }}
            validator={validators.lastName}
            helperText="※実施企業・団体に登録されているお名前（姓）を入力してください"
            givenErrors={lastNameErrorMessages}
          />
          <TextForm
            title="名"
            placeholder="例：太郎"
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
              setFirstNameErrorMessages([]);
            }}
            validator={validators.firstName}
            helperText="※実施企業・団体に登録されているお名前（名）を入力してください"
            givenErrors={firstNameErrorMessages}
          />
          <Stack spacing={1}>
            <Typography variant="subtitle2">生年月日</Typography>
            <BirthdayForm
              value={birthday}
              givenErrors={birthdayErrorMessages}
              birthdaySetAction={(value) => {
                setBirthday(value);
                setBirthdayErrorMessages([]);
              }}
              birthdayValidator={validators.birthday}
            />
          </Stack>
        </Stack>
      </Stack>
      <Stack>
        <Button
          variant="contained"
          size="large"
          sx={{ mx: 4, mb: 16 }}
          onClick={handleClickJoin}
          disabled={Object.values(validators).some((v) => v()) || isLocked}
        >
          参加する
        </Button>
      </Stack>
    </>
  );
};
